import React, { useEffect, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

const SOURCE_LABELS = { products: 'Products', product_types: 'Product lines (configurator catalog)', categories: 'Product categories', fabric_collections: 'Fabric collections', fabric_colors: 'Fabric colors', gallery_projects: 'Gallery projects', media: 'Media library (gallery images)', blog: 'Blog articles', faqs: 'FAQ items', pages: 'Website pages' };

// Lets the admin bind a section to existing back-end data (auto-latest or hand-picked).
export const DataSourcePicker = ({ api, spec, source, onChange }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const type = source?.type || spec.default || spec.types[0];
  const mode = source?.ids?.length ? 'manual' : 'auto';

  useEffect(() => {
    if (!type) return;
    api.dataSource(type).then(setItems).catch(() => setItems([]));
  }, [type, api]);

  const set = (patch) => onChange({ type, limit: 12, ...(source || {}), ...patch });
  const toggle = (id) => {
    const ids = source?.ids || [];
    set({ ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
  };
  const filtered = items.filter((i) => !q || (i.name || i.title || i.question || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-3" data-testid="data-source-picker">
      {spec.types.length > 1 && (
        <div><Label className="text-xs">Show</Label>
          <select className="w-full border border-neutral-300 px-3 py-2 text-sm bg-white" value={type} onChange={(e) => onChange({ type: e.target.value, limit: 12, ids: [] })} data-testid="source-type-select">
            {spec.types.map((t) => <option key={t} value={t}>{SOURCE_LABELS[t] || t}</option>)}
          </select></div>
      )}
      {!source && <button className="text-xs text-amber-700 underline" onClick={() => onChange({ type, limit: 12 })} data-testid="source-enable-btn">Connect to {SOURCE_LABELS[type] || type}</button>}
      {source && (
        <>
          <div className="flex gap-2 text-sm">
            <button onClick={() => set({ ids: [] })} className={`flex-1 border px-3 py-1.5 ${mode === 'auto' ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-300'}`} data-testid="source-mode-auto">Automatic (latest)</button>
            <button onClick={() => set({ ids: source.ids?.length ? source.ids : [filtered[0]?.id].filter(Boolean) })} className={`flex-1 border px-3 py-1.5 ${mode === 'manual' ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-300'}`} data-testid="source-mode-manual">Pick specific</button>
          </div>
          {mode === 'auto' && (
            <div><Label className="text-xs">How many to show</Label><Input type="number" min={1} max={48} value={source.limit || 12} onChange={(e) => set({ limit: Number(e.target.value) })} data-testid="source-limit-input" /></div>
          )}
          {mode === 'manual' && (
            <div>
              <div className="relative mb-2"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-neutral-400" /><Input className="pl-8 h-9" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
              <div className="max-h-64 overflow-y-auto border border-neutral-200 divide-y">
                {filtered.map((i) => {
                  const on = source.ids?.includes(i.id);
                  return (
                    <button key={i.id} onClick={() => toggle(i.id)} className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-neutral-50 ${on ? 'bg-amber-50' : ''}`} data-testid={`source-item-${i.id}`}>
                      <span className={`w-4 h-4 border flex items-center justify-center ${on ? 'bg-amber-500 border-amber-500 text-white' : 'border-neutral-300'}`}>{on && <Check className="w-3 h-3" />}</span>
                      {(i.image || i.hex_color) && <span className="w-8 h-8 bg-neutral-100 bg-cover bg-center flex-shrink-0" style={{ backgroundImage: i.image ? `url(${i.image})` : undefined, backgroundColor: i.hex_color }} />}
                      <span className="truncate">{i.display_name || i.name || i.title || i.question}</span>
                    </button>
                  );
                })}
                {filtered.length === 0 && <p className="p-3 text-xs text-neutral-400">Nothing found</p>}
              </div>
              <p className="text-xs text-neutral-500 mt-1">{source.ids?.length || 0} selected · order follows selection</p>
            </div>
          )}
          <button className="text-xs text-neutral-500 underline" onClick={() => onChange(null)}>Disconnect data</button>
        </>
      )}
    </div>
  );
};
